import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Dialog,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateTaskMutation,
  useGetCustomerQuery,
  useGetTeamQuery,
  useGetAllTaskSubjectQuery,
  useGetAllPolicyGroupByQuery,
  useGetTaskCategoryQuery,
  useGetAllHolydaysArrayQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import {
  IMeeting,
  ICustomer,
  ITeam,
  IPolicy,
  IMeetingCategory2,
  CustomerOptionString,
  ITasks,
} from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import Autocomplete from "@mui/material/Autocomplete";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { ReminderBeforeOption } from "../../../Core/Enum/enum";
import { BiSolidMessageSquareAdd } from "react-icons/bi";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import TimeDropdown from "../../../Components/CustomTimePicker/TimeDropdown";

interface IMeetingPopUp {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data: ITasks;
}

interface CustomerOption {
  label: string;
  value: number;
}

const capitalizeFirstLetter = (name: string | null | undefined) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const Department = ({
  openModel = false,
  handleCloseDialog,
  data,
}: IMeetingPopUp) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [newDepartment, { isLoading }] = useCreateTaskMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | "">("");

  const { data: clientData, isLoading: cilentIsLoading } = useGetCustomerQuery({
    searchText: "",
    id: "",
  });

  const { data: staffData, isLoading: staffIsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const { data: meetingAllData, isLoading: meetingAllIsLoading } =
    useGetAllTaskSubjectQuery({
      searchText: "",
      id: "",
    });

  const { data: policyData, isLoading: policyIsLoading } =
    useGetAllPolicyGroupByQuery({
      searchText: "",
      id: "",
      policyCustomerId: selectedCustomerId,
    });

  const { data: holydaysData, isLoading: holidaysIsLoading } =
    useGetAllHolydaysArrayQuery({
      searchText: "",
      id: "",
    });

  const {
    data: getAllMeetingCategoryData,
    isLoading: meetingCategoryIsLoading,
  } = useGetTaskCategoryQuery({
    searchText: "",
  });

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const cliendList = useMemo(() => {
    return (clientData?.data ?? []) as ICustomer[];
  }, [clientData?.data]);

  const staffList = useMemo(() => {
    return (staffData?.data ?? []) as ITeam[];
  }, [staffData?.data]);

  const policyList = useMemo(() => {
    return (policyData?.data ?? []) as IPolicy[];
  }, [policyData?.data]);

  const meetingCategoryList = useMemo(() => {
    return (getAllMeetingCategoryData?.data ?? []) as IMeetingCategory2[];
  }, [getAllMeetingCategoryData?.data]);

  const customerOptions = useMemo(() => {
    if (!Array.isArray(cliendList)) return [];
    return cliendList.map((cust) => ({
      label: `${capitalizeFirstLetter(
        cust.customerFirstName
      )} ${capitalizeFirstLetter(cust.customerLastName)}`,
      value: cust.id,
    }));
  }, [cliendList]);

  const staffOptions = useMemo(() => {
    if (!Array.isArray(staffList)) return [];
    return staffList.map((cust) => ({
      label: `${capitalizeFirstLetter(
        cust.teamFirstName
      )} ${capitalizeFirstLetter(cust.teamLastName)}`,
      value: cust.id,
    }));
  }, [staffList]);

  const policyOptions = useMemo(() => {
    if (!Array.isArray(policyList)) return [];
    return policyList.map((cust) => ({
      label: `${cust.policyNumber}`,
      value: cust.id,
    }));
  }, [policyList]);

  const meetingCategoryOptions = useMemo(() => {
    if (!Array.isArray(meetingCategoryList)) return [];
    return meetingCategoryList.map((cust) => ({
      label: `${cust.name}`,
      value: cust.id,
    }));
  }, [meetingCategoryList]);

  const reminderBeforOptions: CustomerOptionString[] = Object.values(
    ReminderBeforeOption
  ).map((day) => ({
    label: day,
    value: day,
  }));

  const formik = useFormik({
    initialValues: {
      id: data.id,
      staffId: data.staffId || "",
      clientId: data.clientId || "",
      subject: data.subject || "",
      start_Date: dayjs(data.start_Date.toString()) || "",
      end_Date: dayjs(data.end_Date.toString()) || "",
      policyId: data.policyId || "",
      categoryId: data.categoryId || "",
      emailReminder: data.emailReminder || false,
      smsReminder: data.smsReminder || false,
      reminderBeforHours: data.reminderBeforHours || "",
      createNotification: data.createNotification || false,
      adminReminder: data.adminReminder || false,
      note: data.note || "",
    },
    onSubmit: async (values) => {
      try {
        const temData = {
          id: values?.id,
          staffId: values.staffId,
          clientId: values.clientId,
          subject: capitalizeFirstLetter(values.subject),
          start_Date: values.start_Date.format("YYYY-MM-DD"),
          end_Date: values.end_Date.format("YYYY-MM-DD"),
          note: values.note,
          policyId: values.policyId,
          categoryId: values.categoryId,
          emailReminder: values.emailReminder,
          smsReminder: values.smsReminder,
          reminderBeforHours: values.reminderBeforHours,
          createNotification: values.createNotification,
          adminReminder: values.adminReminder,
        };
        const addCompanyResponse = await newDepartment(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return formik.values.staffId === "" ||
      formik.values.subject === "" ||
      !formik.values.start_Date ||
      !formik.values.end_Date ||
      formik.values.categoryId === "" ||
      !dayjs(formik.values.start_Date).isValid() ||
      !dayjs(formik.values.end_Date).isValid()
      ? false
      : true;
  }, [formik.values]);

  useEffect(() => {
    if (formik.values.clientId) {
      setSelectedCustomerId(formik.values.clientId);
      // formik.setFieldValue("policyId", "");
    }
  }, [formik.values.clientId]);

  useEffect(() => {
    const exists = policyOptions.some(
      (opt) => opt.value === Number(formik.values.policyId)
    );

    if (!exists && formik.values.policyId) {
      formik.setFieldValue("policyId", "");
    }
  }, [policyOptions, formik.values.policyId]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobile}
      fullWidth={isMobile}
      maxWidth="lg"
    >
      <Grid className={classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          New Task
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Subject{" "}
                              <span className={classes.requiedfield}>*</span>
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Autocomplete
                              freeSolo
                              options={
                                meetingAllData?.data?.filter(Boolean) ?? []
                              }
                              loading={meetingAllIsLoading}
                              inputValue={formik.values.subject}
                              onInputChange={(_, value) =>
                                formik.setFieldValue("subject", value)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Subject"
                                  size="small"
                                  multiline
                                  rows={2}
                                  className={classes.textField}
                                  InputProps={{
                                    ...params.InputProps,
                                    sx: { fontSize: 14 },
                                  }}
                                  InputLabelProps={{
                                    sx: { fontSize: 14 },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={6} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Category{" "}
                              <span className={classes.requiedfield}>*</span>
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Autocomplete
                              options={meetingCategoryOptions}
                              loading={meetingCategoryIsLoading}
                              getOptionLabel={(option: CustomerOption) =>
                                option.label
                              }
                              value={
                                meetingCategoryOptions.find(
                                  (opt) =>
                                    opt.value ===
                                    Number(formik.values.categoryId)
                                ) || null
                              }
                              onChange={(
                                _,
                                newValue: CustomerOption | null
                              ) => {
                                formik.setFieldValue(
                                  "categoryId",
                                  newValue?.value || ""
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Meeting Category .."
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  className="textField"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {meetingCategoryIsLoading ? (
                                          <CircularProgress size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              sx={{ width: "100%" }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={6} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Staff{" "}
                              <span className={classes.requiedfield}>*</span>
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Autocomplete
                              options={staffOptions}
                              loading={staffIsLoading}
                              getOptionLabel={(option: CustomerOption) =>
                                option.label
                              }
                              value={
                                staffOptions.find(
                                  (opt) =>
                                    opt.value === Number(formik.values.staffId)
                                ) || null
                              }
                              onChange={(
                                _,
                                newValue: CustomerOption | null
                              ) => {
                                formik.setFieldValue(
                                  "staffId",
                                  newValue?.value || ""
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Staff .."
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  className="textField"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {staffIsLoading ? (
                                          <CircularProgress size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                              sx={{ width: "100%" }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container spacing={2}>
                          <Grid item lg={6} md={12} sm={12} xs={12}>
                            <Grid container alignItems="center">
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Typography variant="subtitle1">
                                  Client{" "}
                                </Typography>
                              </Grid>
                              <Grid item lg={12} md={12} sm={12} xs={12}>
                                <Autocomplete
                                  options={customerOptions}
                                  loading={cilentIsLoading}
                                  getOptionLabel={(option: CustomerOption) =>
                                    option.label
                                  }
                                  value={
                                    customerOptions.find(
                                      (opt) =>
                                        opt.value ===
                                        Number(formik.values.clientId)
                                    ) || null
                                  }
                                  onChange={(
                                    _,
                                    newValue: CustomerOption | null
                                  ) => {
                                    formik.setFieldValue(
                                      "clientId",
                                      newValue?.value || ""
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Client .."
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                      className="textField"
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {staffIsLoading ? (
                                              <CircularProgress size={20} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                          </>
                                        ),
                                      }}
                                    />
                                  )}
                                  sx={{ width: "100%" }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={6} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Start Date
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={formik.values.start_Date}
                                onChange={(start_Date) =>
                                  formik.setFieldValue("start_Date", start_Date)
                                }
                                // shouldDisableDate={(day) =>
                                //   !!holydaysData?.data?.some((holiday) =>
                                //     day.isSame(dayjs(holiday), "day")
                                //   )
                                // }
                                shouldDisableDate={(day) =>
                                  !!holydaysData?.data?.some((holiday: Dayjs) =>
                                    day.isSame(holiday, "day")
                                  )
                                }
                                slotProps={{
                                  textField: {
                                    placeholder: "Start Date",
                                    size: "small",
                                    sx: {
                                      width: "100%",
                                    },
                                    InputProps: {
                                      sx: {
                                        fontSize: 14,
                                      },
                                    },
                                    InputLabelProps: {
                                      sx: {
                                        fontSize: 14,
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={6} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              End Date
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={formik.values.end_Date}
                                onChange={(end_Date) =>
                                  formik.setFieldValue("end_Date", end_Date)
                                }
                                // shouldDisableDate={(day) =>
                                //   !!holydaysData?.data?.some((holiday) =>
                                //     day.isSame(dayjs(holiday), "day")
                                //   )
                                // }
                                shouldDisableDate={(day) =>
                                  !!holydaysData?.data?.some((holiday: Dayjs) =>
                                    day.isSame(holiday, "day")
                                  )
                                }
                                slotProps={{
                                  textField: {
                                    placeholder: "End Date",
                                    size: "small",
                                    sx: {
                                      width: "100%",
                                    },
                                    InputProps: {
                                      sx: {
                                        fontSize: 14,
                                      },
                                    },
                                    InputLabelProps: {
                                      sx: {
                                        fontSize: 14,
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Description
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Description .."
                              size="small"
                              multiline
                              rows={8}
                              {...formik.getFieldProps("note")}
                              className={classes.discriptionTextField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid> */}
                    </Grid>
                  </Card>
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Reminder
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                      <Grid item lg={6} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Reminder (Staff)
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formik.values.emailReminder}
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "emailReminder",
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label="Email"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formik.values.smsReminder}
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "smsReminder",
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label="SMS"
                              />
                            </FormGroup>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={4} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Reminder: Task completion due before the deadline.
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Autocomplete
                              options={reminderBeforOptions}
                              getOptionLabel={(option: CustomerOptionString) =>
                                option.label
                              }
                              value={
                                reminderBeforOptions.find(
                                  (opt) =>
                                    opt.value ===
                                    String(formik.values.reminderBeforHours)
                                ) || null
                              }
                              onChange={(
                                _,
                                newValue: CustomerOptionString | null
                              ) => {
                                formik.setFieldValue(
                                  "reminderBeforHours",
                                  newValue?.value || ""
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Remind Before .."
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  className="textField"
                                  InputProps={{
                                    ...params.InputProps,
                                  }}
                                />
                              )}
                              sx={{ width: "100%" }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formik.values.adminReminder}
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "adminReminder",
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label="Admin Reminder"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formik.values.createNotification}
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "createNotification",
                                        e.target.checked
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label="Email notification on create"
                              />
                            </FormGroup>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={
                            isLoading ? (
                              <CircularProgress
                                size={18}
                                className={classes.isLoading}
                              />
                            ) : (
                              <BiSolidMessageSquareAdd />
                            )
                          }
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          Updated
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Department;
